import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  TemplateRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { combineLatest, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { FileStorageItemService } from '@6grain/masofa-monolith-api-client/common/api/fileStorageItem.service';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { TableAction, TableActionsComponent } from '@components/table-actions/table-actions.component';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import {
  BUCKET_FILES_PAGE_SIZE,
  BUCKET_FILES_PAGE_SIZES,
  BUCKET_FILES_SEARCH_DEBOUNCE_MS,
  BucketFileRow,
  buildBucketFilesQuery,
  toBucketFileRow,
} from '@app/pages/admin/orchestration-bucket-files.util';

@Component({
  selector: 'app-orchestration-bucket-files',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DialogModule,
    SecondaryButton,
    DataTableComponent,
    TableActionsComponent,
    OrchestrationTableHeadingComponent,
  ],
  templateUrl: './orchestration-bucket-files.html',
  styleUrl: './orchestration-bucket-files.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationBucketFilesComponent implements OnInit {
  private readonly fileStorage = inject(FileStorageItemService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly pathTemplate = viewChild<TemplateRef<unknown>>('pathTemplate');
  private readonly actionsTemplate = viewChild<TemplateRef<unknown>>('actionsTemplate');

  readonly bucket = input.required<string>();
  readonly refreshTick = input(0);
  readonly closed = output<void>();

  readonly fileSearch = signal('');
  readonly files = signal<BucketFileRow[]>([]);
  readonly total = signal(0);
  readonly loading = signal(true);
  readonly errorMessage = signal('');
  readonly pageFirst = signal(0);
  readonly pageSize = signal(BUCKET_FILES_PAGE_SIZE);
  readonly preview = signal<BucketFileRow | null>(null);
  readonly previewFailed = signal(false);
  readonly previewActions: TableAction[] = [{ action: 'view', tooltip: 'View file' }];

  constructor() {
    effect(() => {
      this.bucket();
      queueMicrotask(() => this.host.nativeElement.scrollIntoView({ block: 'nearest' }));
    });
  }

  readonly emptyMessage = computed(() => {
    if (this.loading() && this.files().length === 0) {
      return 'Loading';
    }
    return this.fileSearch().trim() ? 'No files match this search' : 'No files in this bucket';
  });

  readonly tableColumns = computed<TableColumn[]>(() => [
    { field: 'path', header: 'Path', type: 'custom', customTemplate: this.pathTemplate() },
    { field: 'sizeLabel', header: 'Size', type: 'text' },
    { field: 'contentType', header: 'Content type', type: 'text' },
    { field: 'createdAt', header: 'Created', type: 'date' },
    { field: 'actions', header: 'Actions', type: 'custom', customTemplate: this.actionsTemplate() },
  ]);

  readonly tableConfig = computed<TableConfig>(() => ({
    paginator: true,
    lazy: true,
    rows: this.pageSize(),
    rowsPerPageOptions: [...BUCKET_FILES_PAGE_SIZES],
    keepOrder: true,
    emptyMessage: this.emptyMessage(),
  }));

  ngOnInit(): void {
    const search$ = toObservable(this.fileSearch, { injector: this.injector }).pipe(
      map(value => value.trim()),
      debounceTime(BUCKET_FILES_SEARCH_DEBOUNCE_MS),
      distinctUntilChanged(),
    );
    let listedKey = '';
    let listedBucket: string | null = null;

    combineLatest([
      toObservable(this.bucket, { injector: this.injector }),
      search$,
      toObservable(this.pageFirst, { injector: this.injector }),
      toObservable(this.pageSize, { injector: this.injector }),
      toObservable(this.refreshTick, { injector: this.injector }),
    ]).pipe(
      tap(() => {
        this.loading.set(true);
        this.errorMessage.set('');
      }),
      switchMap(([bucket, search, offset, take]) => {
        const switchedBucket = listedBucket !== null && listedBucket !== bucket;
        listedBucket = bucket;
        const effectiveSearch = switchedBucket ? '' : search;
        if (switchedBucket && this.fileSearch().trim()) {
          this.fileSearch.set('');
        }
        const key = `${bucket}\0${effectiveSearch}`;
        const pageOffset = key === listedKey ? offset : 0;
        if (key !== listedKey) {
          listedKey = key;
          if (offset !== 0) {
            queueMicrotask(() => this.pageFirst.set(0));
          }
          this.files.set([]);
          this.total.set(0);
        }
        const name = bucket.trim();
        if (!name) {
          return of({ ok: true as const, items: [] as BucketFileRow[], total: 0 });
        }
        const query = buildBucketFilesQuery({
          bucket: name,
          search: effectiveSearch,
          take,
          offset: pageOffset,
        });
        return combineLatest([
          this.fileStorage.commonFileStorageItemGetByQueryPost(query),
          this.fileStorage.commonFileStorageItemGetTotalCountPost(query),
        ]).pipe(
          map(([items, total]) => ({
            ok: true as const,
            items: (items ?? []).map(toBucketFileRow).filter((row): row is BucketFileRow => !!row),
            total: Number(total) || 0,
          })),
          catchError(() => of({ ok: false as const, items: [] as BucketFileRow[], total: 0 })),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(result => {
      this.loading.set(false);
      if (!result.ok) {
        this.files.set([]);
        this.total.set(0);
        this.errorMessage.set('Failed to load bucket files');
        return;
      }
      this.files.set(result.items);
      this.total.set(result.total);
    });
  }

  onPage(event: { first?: number; rows?: number }): void {
    const nextSize = event.rows ?? this.pageSize();
    const nextFirst = nextSize === this.pageSize() ? (event.first ?? 0) : 0;
    if (nextSize !== this.pageSize()) {
      this.pageSize.set(nextSize);
    }
    if (nextFirst !== this.pageFirst()) {
      this.pageFirst.set(nextFirst);
    }
  }

  openPreview(row: BucketFileRow): void {
    if (!row.previewUrl) {
      return;
    }
    this.previewFailed.set(false);
    this.preview.set(row);
  }

  closePreview(): void {
    this.preview.set(null);
    this.previewFailed.set(false);
  }

  markPreviewFailed(): void {
    this.previewFailed.set(true);
  }
}
